import { useCallback, useState } from 'react';
import { useDropzone, type DropEvent, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { validateFile } from '@/utils/nodeInputValidation';
import { probeVideoFileBrowserSupport } from '@/utils/fileUploadUtils';
import { log } from '@/logger/logger';
import { getFilteredAcceptedFileTypes, getMimeTypeFromExtension } from '@/utils/fileTypes';
import { assetViewUrl } from '@/utils/assetUrls';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { LOCAL_MODEL_KEY_PREFIX } from '@/consts/localModel.consts';
import { I18N_KEYS } from '@/language/keys';
import { RunType } from '@/enums/run-type.enum';
import type { MediaAsset, UploadAssetResponse } from '@/types/api/assets';
import type { AxiosProgressEvent } from 'axios';

const logger = log.getLogger('useUploadMediaAsset');

// Reserve the last 5% for the server response processing
const MAX_UPLOAD_TRANSFER_PROGRESS = 95;

const extractFileNameFromUrl = (url: string) => {
  try {
    const segment = new URL(url).pathname.split('/').pop() ?? '';
    return decodeURIComponent(segment);
  } catch {
    return url;
  }
};

type UseUploadMediaAssetErrors = 'invalid_url' | 'upload_error' | 'invalid_file_type';

export type FileType = 'image' | 'video' | 'audio' | '3D' | 'unknown';

// Lifecycle hooks for local-file direct load of dropped 3D models:
// render from in-memory bytes now (onLocalRender), then on the background
// upload swap the temp key for the real asset (onUploaded) or roll back (onFailed).
export interface LocalDirectLoadHandlers {
  onLocalRender: (tempKey: string, bytes: Uint8Array, file: File) => void;
  onUploaded: (tempKey: string, asset: MediaAsset) => void;
  onFailed: (tempKey: string) => void;
}

interface UseUploadMediaAssetProps {
  disabled?: boolean;
  onUpload: (uploadData: MediaAsset) => void;
  allowMultiple?: boolean;
  setIsUploading?: (isUploading: boolean) => void;
  // Emphasizing that this can and should be undefined, until a file is chosen
  acceptedFileType: FileType | undefined;
  // override the bad-type snackbar text. callers with no node context (e.g. a modal) pass a generic
  // message so the shared "in this node" copy doesn't leak.
  invalidFileTypeMessage?: string;
  // by default a click anywhere on the dropzone root opens the file dialog. set this when the root
  // has its own controls and only an explicit button should open it.
  noClick?: boolean;
  localDirectLoad?: LocalDirectLoadHandlers;
  runType: RunType;
}

export const useUploadMediaAsset = ({
  disabled = false,
  onUpload,
  allowMultiple = false,
  setIsUploading,
  acceptedFileType,
  invalidFileTypeMessage,
  noClick = false,
  localDirectLoad,
  runType,
}: UseUploadMediaAssetProps) => {
  const { t } = useTranslation();
  const [uploadedFileType, setUploadedFileType] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<UseUploadMediaAssetErrors | null>(null);
  const { updateSnackbarData } = useGlobalStore();
  const handleError = useCallback(
    (error: UseUploadMediaAssetErrors, params?: { invalidFileTypeCount?: number; fileType?: FileType }) => {
      setError(error);
      let text = t(I18N_KEYS.GENERAL.UPLOAD_ERROR);
      switch (error) {
        case 'invalid_url':
          text = t(I18N_KEYS.GENERAL.INVALID_URL);
          break;
        case 'upload_error':
          text = t(I18N_KEYS.GENERAL.UPLOAD_ERROR);
          break;
        case 'invalid_file_type': {
          if (invalidFileTypeMessage) {
            text = invalidFileTypeMessage;
            break;
          }
          const invalidCount = params?.invalidFileTypeCount ?? 1;
          // If fileType is not known (undefined or 'unknown'), show a generic error message
          if (!params?.fileType || params.fileType === 'unknown') {
            text = t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.UPLOAD_ERROR_INVALID_FILE_TYPE_GENERIC, {
              count: invalidCount,
            });
          } else {
            let fileTypeKey: keyof typeof I18N_KEYS.GENERAL.FILE_TYPES = 'IMAGE';
            switch (params.fileType) {
              case 'video':
                fileTypeKey = 'VIDEO';
                break;
              case 'audio':
                fileTypeKey = 'AUDIO';
                break;
              case '3D':
                fileTypeKey = 'THREE_D';
                break;
              default:
                fileTypeKey = 'IMAGE';
                break;
            }
            text = t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.UPLOAD_ERROR_INVALID_FILE_TYPE, {
              count: invalidCount,
              fileType: t(I18N_KEYS.GENERAL.FILE_TYPES[fileTypeKey], {
                // In this scenario we always want the plural form
                count: 2,
                context: 'lower',
              }),
            });
          }
          break;
        }
      }
      updateSnackbarData({
        text,
        severity: 'error',
        icon: null,
        isOpen: true,
      });
    },
    [t, updateSnackbarData, invalidFileTypeMessage],
  );

  const isInProgress = uploadProgress > 0 && uploadProgress < 100;

  const uploadSuccess = useCallback(
    (uploadData: MediaAsset) => {
      // Read the store, not a hook value: mergeResolvedAssets ran moments ago in this same
      // callback, so a subscribed value would still be the pre-merge render's.
      setPreviewImage(assetViewUrl(uploadData, useWorkflowStore.getState().resolvedAssets) || uploadData.thumbnailUrl);
      setUploadedFileType(uploadData.type);
      onUpload(uploadData);
    },
    [onUpload],
  );

  const onUploadProgress = useCallback((progressEvent: AxiosProgressEvent) => {
    const total = progressEvent.total ?? progressEvent.loaded;
    const progress =
      total > 0 ? Math.max(1, Math.round((progressEvent.loaded / total) * MAX_UPLOAD_TRANSFER_PROGRESS)) : 1;
    setUploadProgress(progress);
  }, []);

  const handleUploadStart = useCallback((fileType: string, previewSrc: string) => {
    setError(null);
    setUploadedFileType(fileType);
    setPreviewImage(previewSrc);
  }, []);

  const resetUpload = useCallback(() => {
    setError(null);
    setUploadedFileType('');
    setPreviewImage('');
    setUploadProgress(0);
  }, []);

  const executeUpload = useCallback(
    async ({
      uploadData,
      params,
      fileName,
      localKey,
      updateUploadProgress,
    }: {
      uploadData?: FormData;
      params?: Record<string, string>;
      fileName?: string;
      localKey?: string;
      updateUploadProgress?: (event: AxiosProgressEvent) => void;
    }) => {
      // Locally rendered 3D models have `localKey`. 3D models renders bytes immediately
      // and the server upload continues in the background so we don't track their progress in the foreground.
      if (!localKey) {
        setUploadProgress((progress) => (progress === 100 ? 1 : Math.max(1, progress)));
      }
      try {
        logger.info('file upload start', { uploadData, params });
        const response = await safeAxios.post<UploadAssetResponse>(
          API_ROUTES.assets.uploadAsset(),
          uploadData ?? null,
          {
            params: { ...params, runType },
            // Direct load: the model is already rendered, so the upload runs in the
            // background — don't drive uploadProgress (it would re-trigger isInProgress
            // and re-show the progress overlay over the visible model).
            onUploadProgress: localKey ? undefined : (updateUploadProgress ?? onUploadProgress),
          },
        );

        useWorkflowStore.getState().mergeResolvedAssets(response.data.resolvedAssets ?? {});

        if (!validateFile(response.data.asset)) {
          throw new Error('Invalid file response: ' + JSON.stringify(response.data.asset));
        }

        const asset = fileName ? { ...response.data.asset, name: fileName } : response.data.asset;
        // Direct load already rendered the model; just re-key to the CDN asset
        // instead of running the normal upload-success (which would add a new file).
        if (localKey && localDirectLoad) {
          localDirectLoad.onUploaded(localKey, asset);
        } else {
          uploadSuccess(asset);
          if (!updateUploadProgress) setUploadProgress(100);
        }
      } catch (err) {
        if (!localKey && !updateUploadProgress) setUploadProgress(0);
        logger.error('Error uploading:', err);
        handleError('upload_error');
        // Direct load: drop the locally-rendered model since it has no server backing.
        if (localKey && localDirectLoad) {
          localDirectLoad.onFailed(localKey);
        }
        throw err;
      }
    },
    [handleError, onUploadProgress, uploadSuccess, localDirectLoad, runType],
  );

  const detectFileType = useCallback((file: File): { type: FileType; previewSrc: string } => {
    const fileName = file.name.toLowerCase();

    if (file.type.startsWith('video')) {
      return { type: 'video', previewSrc: URL.createObjectURL(file) };
    } else if (file.type.startsWith('image')) {
      return { type: 'image', previewSrc: URL.createObjectURL(file) };
    } else if (file.type.startsWith('audio')) {
      return { type: 'audio', previewSrc: '/audio.png' };
    } else if (
      fileName.endsWith('.glb') ||
      fileName.endsWith('.obj') ||
      fileName.endsWith('.fbx') ||
      fileName.endsWith('.ply')
    ) {
      return { type: '3D', previewSrc: '' };
    } else if (fileName.endsWith('.heic')) {
      return { type: 'image', previewSrc: '' };
    }

    return { type: 'unknown', previewSrc: '' };
  }, []);

  const uploadFile = useCallback(
    async (
      file: File,
      fileInfo?: { type: FileType; previewSrc: string },
      updateUploadProgress?: (event: AxiosProgressEvent) => void,
    ) => {
      const { type, previewSrc } = fileInfo || detectFileType(file);

      // Direct load only applies to 3D drops where the caller wired up the handlers.
      const useLocalDirectLoad = type === '3D' && Boolean(localDirectLoad);

      // Bail out early if the browser can't decode this video — uploading would just produce
      // an asset that displays as a static frame on the canvas with no playback (e.g. ProRes-in-MOV).
      // Snackbar-only (no setError): keeps any previously-uploaded file's preview intact.
      if (type === 'video') {
        const supported = await probeVideoFileBrowserSupport(file);
        if (!supported) {
          updateSnackbarData({
            text: t(I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.UNSUPPORTED_VIDEO_FORMAT),
            severity: 'error',
            icon: null,
            isOpen: true,
          });
          return;
        }
      }

      handleUploadStart(type, previewSrc);

      // Render the dropped bytes immediately under a temp key and clear the
      // spinner; the upload below then proceeds in the background. On read failure
      // localKey stays undefined, so we fall back to the normal upload→download path.
      let localKey: string | undefined;
      if (useLocalDirectLoad && localDirectLoad) {
        const candidateKey = `${LOCAL_MODEL_KEY_PREFIX}${crypto.randomUUID()}`;
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          localDirectLoad.onLocalRender(candidateKey, bytes, file);
          localKey = candidateKey;
          setIsUploading?.(false);
        } catch (e) {
          logger.error('Failed to read local 3D file for direct load', e);
        }
      }

      const formData = new FormData();
      formData.append('file', file);
      const mimeType = file.type || getMimeTypeFromExtension(file.name);
      if (mimeType) {
        formData.append('type', mimeType);
      }
      await executeUpload({ uploadData: formData, fileName: file.name, localKey, updateUploadProgress });
    },
    [detectFileType, handleUploadStart, executeUpload, t, updateSnackbarData, localDirectLoad, setIsUploading],
  );

  const onLinkUpload = useCallback(
    async (link: string) => {
      const urlPattern = /^(https?:\/\/[^\s$.?#].[^\s]*)$/;
      if (!urlPattern.test(link)) {
        logger.error('Invalid URL');
        handleError('invalid_url');
        return;
      }

      handleUploadStart('image', link);
      await executeUpload({ params: { file: link }, fileName: extractFileNameFromUrl(link) });
    },
    [handleUploadStart, executeUpload, handleError],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles?: FileRejection[], event?: DropEvent) => {
      if (event) {
        // Ensure the event doesn't bubble up to the Flow component
        const e = event as unknown as DragEvent;
        e.stopPropagation();
        e.preventDefault();
      }
      const filesToUpload: { file: File; fileInfo?: { type: FileType; previewSrc: string } }[] = [];
      let totalInvalidFileCount = 0;
      let errorFileType: FileType | undefined = acceptedFileType;

      if (acceptedFiles.length > 0) {
        if (allowMultiple) {
          let selectedFileType = acceptedFileType;
          acceptedFiles.forEach((file) => {
            const { type, previewSrc } = detectFileType(file);
            // This handles the initial upload - we set the accepted file type to the first file type
            // and check the following files to ensure they are of the same type.
            if (selectedFileType && selectedFileType !== type) {
              totalInvalidFileCount++;
              errorFileType = selectedFileType;
              return;
            }
            selectedFileType = type;
            filesToUpload.push({ file, fileInfo: { type, previewSrc } });
          });
        } else {
          const file = acceptedFiles[0];
          filesToUpload.push({ file });
        }
      }

      // Add rejected files to the total invalid count
      if (rejectedFiles && rejectedFiles.length > 0) {
        totalInvalidFileCount += rejectedFiles.length;
      }

      // Show a single error message if there are any invalid files
      if (totalInvalidFileCount > 0) {
        handleError('invalid_file_type', {
          invalidFileTypeCount: totalInvalidFileCount,
          fileType: errorFileType,
        });
      }

      setUploadProgress(0);
      const totalBytes = filesToUpload.reduce((total, { file }) => total + file.size, 0);
      const uploadedBytesByFile = new Map<File, number>();
      const uploadPromises = filesToUpload.map(({ file, fileInfo }) =>
        uploadFile(file, fileInfo, (event) => {
          const total = event.total ?? file.size;
          const fraction = total > 0 ? Math.min(1, event.loaded / total) : 0;
          // Keep completed files and ignore retry resets so batch progress cannot move backward.
          uploadedBytesByFile.set(file, Math.max(uploadedBytesByFile.get(file) ?? 0, fraction * file.size));
          const uploadedBytes = [...uploadedBytesByFile.values()].reduce((sum, bytes) => sum + bytes, 0);
          setUploadProgress(
            Math.max(1, Math.round((uploadedBytes / Math.max(1, totalBytes)) * MAX_UPLOAD_TRANSFER_PROGRESS)),
          );
        }),
      );

      setIsUploading?.(true);
      // wait for all files to finish uploading before setting the uploading state to false
      void Promise.allSettled(uploadPromises).then((results) => {
        const allUploaded =
          results.length > 0 &&
          results.every((result) => result.status === 'fulfilled') &&
          uploadedBytesByFile.size === filesToUpload.length;
        setUploadProgress(allUploaded ? 100 : 0);
        setIsUploading?.(false);
      });
    },
    [setIsUploading, allowMultiple, acceptedFileType, detectFileType, uploadFile, handleError],
  );

  /**
   *
   * @returns a list of accepted file types based on the accepted file type or current file type.
   * If none is provided, return all accepted file types.
   */
  const getAcceptedFileTypesToUpload = useCallback(() => {
    const fileTypesValues = Object.values(getFilteredAcceptedFileTypes(acceptedFileType)).flat();
    return fileTypesValues.join(',');
  }, [acceptedFileType]);

  const dropzoneState = useDropzone({
    onDrop,
    accept: getFilteredAcceptedFileTypes(acceptedFileType),
    multiple: true,
    disabled: disabled || isInProgress,
    preventDropOnDocument: true,
    noClick,
  });

  return {
    dropzoneState,
    error,
    isInProgress,
    onLinkUpload,
    previewImage,
    resetUpload,
    uploadFile,
    uploadProgress,
    uploadedFileType,
    getAcceptedFileTypesToUpload,
  };
};
